import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Cable, Lock, RefreshCw } from "lucide-react";
import { GithubIcon as Github } from "@/components/icons";

function StatMini({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
}) {
  return (
    <div className="rounded-lg bg-muted/30 p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
    </div>
  );
}

export function GitHubIntegration() {
  const utils = trpc.useUtils();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: status, isLoading: statusLoading } =
    trpc.github.status.useQuery();
  const { data: installations, isLoading: installationsLoading } =
    trpc.github.listInstallations.useQuery();
  const { data: repositories, isLoading: repositoriesLoading } =
    trpc.github.listRepositories.useQuery({});
  const connectUrlQuery = trpc.github.connectUrl.useQuery(undefined, {
    enabled: false,
  });
  const syncMutation = trpc.github.syncInstallation.useMutation({
    onSuccess: () => {
      toast.success("GitHub installation synced");
      utils.github.status.invalidate();
      utils.github.listInstallations.invalidate();
      utils.github.listRepositories.invalidate();
    },
    onError: (err) => {
      toastTrpcError(err, "GitHub connection failed");
      utils.github.status.invalidate();
      utils.github.listInstallations.invalidate();
      utils.github.listRepositories.invalidate();
    },
  });

  const handleConnect = async () => {
    try {
      const result = await connectUrlQuery.refetch();
      if (result.error) throw result.error;
      const url = result.data?.url;
      if (!url) throw new Error("GitHub install URL not available");
      window.location.href = url;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to start GitHub connect flow",
      );
    }
  };

  useEffect(() => {
    const githubState = searchParams.get("github");
    if (!githubState) return;

    const message = searchParams.get("message");
    if (githubState === "connected") {
      toast.success("GitHub connected");
      utils.github.status.invalidate();
      utils.github.listInstallations.invalidate();
      utils.github.listRepositories.invalidate();
    } else if (githubState === "error") {
      toastTrpcError(message, "GitHub connection failed");
    }

    const next = new URLSearchParams(searchParams);
    next.delete("github");
    next.delete("message");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, utils.github]);

  const isLoading =
    statusLoading || installationsLoading || repositoriesLoading;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-md">
              <Github className="h-4 w-4" />
              GitHub
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Connect a GitHub App installation to use private repositories for
              project bootstrap and upcoming PR review workflows.
            </p>
          </div>
          <Button
            onClick={handleConnect}
            disabled={!status?.configured || connectUrlQuery.isFetching}
          >
            {connectUrlQuery.isFetching ? "Opening..." : "Connect GitHub"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!status?.configured && !statusLoading && (
          <div className="rounded-lg bg-mark-important/10 p-3 text-sm text-muted-foreground">
            GitHub App credentials are not configured on this server yet. Set
            the `GITHUB_APP_*` env vars before connecting.
          </div>
        )}

        {isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-3">
            <StatMini
              label="Configured"
              value={status?.configured ? "Yes" : "No"}
              icon={Github}
            />
            <StatMini
              label="Installations"
              value={String(status?.installationCount ?? 0)}
              icon={Cable}
            />
            <StatMini
              label="Repositories"
              value={String(status?.repositoryCount ?? 0)}
              icon={Lock}
            />
          </div>
        )}

        {!!installations?.length && (
          <div className="divide-y divide-border/60 rounded-lg bg-muted/20">
            {installations.map((installation) => (
              <div
                key={installation.id}
                className="flex items-center justify-between gap-3 p-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">
                      {installation.accountLogin ?? "Unknown account"}
                    </p>
                    {installation.status === "active" ? (
                      <Badge
                        variant="outline"
                        className="rounded-sm border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                      >
                        Active
                      </Badge>
                    ) : installation.status === "suspended" ? (
                      <Badge
                        variant="outline"
                        className="rounded-sm border-amber-500/30 bg-amber-500/10 text-amber-400"
                      >
                        Suspended
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="rounded-sm">
                        Removed
                      </Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {installation.targetType ?? "GitHub"} ·{" "}
                    {installation.repositoryCount} repos
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {installation.status !== "active" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleConnect}
                      disabled={
                        !status?.configured || connectUrlQuery.isFetching
                      }
                    >
                      Re-authorize
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      syncMutation.mutate({ installationId: installation.id })
                    }
                    disabled={syncMutation.isPending}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Sync
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!!repositories?.length && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              Connected repositories
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {repositories.slice(0, 8).map((repository) => (
                <div key={repository.id} className="rounded-lg bg-muted/30 p-3">
                  <p className="text-sm font-medium">{repository.fullName}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {repository.isPrivate ? "Private" : "Public"}
                    {repository.defaultBranch
                      ? ` · ${repository.defaultBranch}`
                      : ""}
                  </p>
                </div>
              ))}
            </div>
            {repositories.length > 8 && (
              <p className="text-xs text-muted-foreground">
                Showing 8 of {repositories.length} repositories.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
